import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import ThreeMeshUI from "three-mesh-ui";

import { cloneObject } from "./cloneObject";
import { setIntersectsObjects } from "./raycaster";
import { computeBoundsTree } from "three-mesh-bvh";
import gsap from "gsap";
import { returnThreeColor } from "../helpers/returnThreeColor";
import { bSizeToVector3 } from "../helpers/bSizeToVector3";
import { getActiveValues } from "../helpers/getActiveValues";
import { expandByMorphMesh } from "./addProductCard";

class Building {
    constructor(gltfModel, scene) {
        this._scene = scene;
        this._gltfModel = gltfModel;
        this.floor = this._gltfModel.getObjectByName("floor");
        this.wallTop = this._gltfModel.getObjectByName("wall_top");
        this.wallRight = this._gltfModel.getObjectByName("wall_right");
        this.wallBot = this._gltfModel.getObjectByName("wall_bot");
        this.wallLeft = this._gltfModel.getObjectByName("wall_left");
        this._column = this._gltfModel.getObjectByName("column");
        this._columns = [];

        this._objects = [];

        this._all = [];
        this._vertical_only = [];
        this._horizontal_only = [];

        this._wallDepth = this.wallTop.scale.y;
        this._floorDepth = this.floor.scale.y;

        this._width = this.floor.scale.x - this._wallDepth;
        this._depth = this.floor.scale.z - this._wallDepth;
        this._height = this.floor.scale.y; // -this._floorDepth

        this._wallColor = this.wallTop.children[0].material.color;
        this._floorColor = this.floor.children[0].material.color;
        this._defaultColor = this.wallTop.children[0].material.color;

        this._is2dMode = false;

        this._sizeLines = {};
        this._lineLimiterLength = 0.2;

        this._lineWidth = 0.8;
        this._sizeColor = 0x000000;

        this.blueColor = 0x3261ec;
        this.redColor = 0xff0000;

        this.DEFAULT_ROOM_COLORS = {
            walls: "#fafafa",
            floor: "#314b6d",
        };

        this._hitboxCache = new Map(); // cache по hitbox.uuid -> { geometry, localBBox, worldBBox, lastMatrix }
        this._parentIntersectState = new Map(); // parent.uuid -> boolean (предыдущее состояние)
        this._lastHitboxSnapshot = { uuidsKey: "", count: 0 }; // чтобы быстро пропускать, если ничего не менялось

        this.#init();
    }

    #init() {
        this._scene.add(this._gltfModel);
        this._column.visible = false;

        this._column.traverse((_) => {
            _.castShadow = true;
            _.receiveShadow = true;
        });

        this._gltfModel.traverse((_) => {
            _.castShadow = true;
            _.receiveShadow = true;
        });

        this.wallRight.position.x = this._width / 2000 + this._wallDepth / 2000 - this._wallDepth / 10000;
        this.wallLeft.position.x = this._width / 2000 - this._wallDepth / 2000 + this._wallDepth / 10000;

        this.wallTop.rotation.y = Math.PI / 2;
        this.wallBot.rotation.y = Math.PI / 2;
        this.wallTop.position.z = -this._depth / 2000 + this._wallDepth / 2000 - this._wallDepth / 10000;
        this.wallBot.position.z = this._depth / 2000 - this._wallDepth / 2000 + this._wallDepth / 10000;

        this.darkenMultiplier = 0.2;

        this.changeWallsColor(returnThreeColor(this.DEFAULT_ROOM_COLORS.walls));
        this.changeFloorColor(returnThreeColor(this.DEFAULT_ROOM_COLORS.floor));
        this.buildingInfo;
    }

    set width(width) {
        if (typeof width !== "number") throw new Error("width is not a number!");
        if (width == this._width) return;

        this._width = width;
        this._applySizesToBuilding();
    }
    set depth(depth) {
        if (typeof depth !== "number") throw new Error("depth is not a number!");
        if (depth == this._depth) return;

        this._depth = depth;
        this._applySizesToBuilding();
    }
    set height(height) {
        if (typeof height !== "number") throw new Error("height is not a number!");
        if (height == this._height) return;

        this._height = height;
        this._applySizesToBuilding(true);
    }
    set wallDepth(wallDepth) {
        if (typeof wallDepth !== "number") throw new Error("wallDepth is not a number!");

        this._wallDepth = wallDepth / 1000;
        //this._gltfModel.traverse(_ => _.position.y = this._wallDepth / 2)
        this._applySizesToBuilding();
    }
    // TODO: Remove
    set floorDepth(_) {
        this._floorDepth = _;
        this.floor.scale.y = _;
    }

    get width() {
        return this._width;
    }
    get depth() {
        return this._depth;
    }
    get height() {
        return this._height;
    }
    get wallDepth() {
        return this._wallDepth;
    }

    get walls() {
        return [this.wallTop, this.wallRight, this.wallBot, this.wallLeft];
    }

    get columns() {
        return this._columns;
    }

    addObject(obj) {
        if (this._all.some((el) => el.uuid === obj.uuid) || this._objects.some((el) => el.uuid === obj.uuid)) return;

        this._objects.push(obj);
        this._all.push(obj);
    }

    removeObject(object) {
        if (!object) return;

        // удалить из сцены
        object.removeFromParent();

        // удалить из всех массивов
        this._objects = this._objects.filter((o) => o.id !== object.id);
        this._all = this._all.filter((o) => o.id !== object.id);
    }

    addVerticalObj(obj) {
        if (this._vertical_only.some((el) => el.uuid === obj.uuid)) return;
        this._vertical_only.push(obj);
        setIntersectsObjects([
            ...this._all,
            ...this.walls,
            this.floor,
            ...this._vertical_only,
            ...this._horizontal_only,
        ]);
    }
    addHorizontalObj(obj) {
        if (this._horizontal_only.some((el) => el.uuid === obj.uuid)) return;
        this._horizontal_only.push(obj);
        setIntersectsObjects([
            ...this._all,
            ...this.walls,
            this.floor,
            ...this._vertical_only,
            ...this._horizontal_only,
        ]);
    }

    addColumn(width, depth, position = new THREE.Vector3(), rotation = new THREE.Euler(0, 0, 0, "XYZ")) {
        const newColumn = this._column.clone();
        newColumn.children[0].material = this._column.children[0].material.clone();
        newColumn.children[1].material = this._column.children[1].material.clone();
        this._scene.add(newColumn);
        newColumn.visible = true;
        newColumn.position.copy(position);
        newColumn.rotation.copy(rotation);

        newColumn.scale.y = this.height;
        newColumn.scale.x = width;
        newColumn.scale.z = depth;

        // После применения scale — обновляем матрицу и считаем bounding box
        newColumn.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(newColumn);
        const size = new THREE.Vector3();
        box.getSize(size);
        newColumn.userData.bSize = size;

        this._columns.push(newColumn);
        this._all.push(newColumn);
        this._cutColumnSizeByBuildingSize(newColumn);

        this.changeWallsColor(this._wallColor);

        return newColumn;
    }
    copyColumn(column) {
        if (!column) return;
        const newColumn = this.addColumn(column.scale.x, column.scale.z);
        return newColumn;
    }
    copyObject(object) {
        if (!object) return;

        const objectCopy = cloneObject(object);

        bSizeToVector3(objectCopy);

        servObj.scene.add(objectCopy);
        // objectCopy.position.set(0, 0.4466414749622345, 0);
        objectCopy.position.set(0, 0, 0);

        this._objects.push(objectCopy);
        this._all.push(objectCopy);
        this._cutColumnSizeByBuildingSize(objectCopy);

        return objectCopy;
    }
    rotateColumn(column) {
        if (!column) return;
        const _ = column.scale.x;
        column.scale.x = column.scale.z;
        column.scale.z = _;
        this._cutColumnSizeByBuildingSize(column);
    }
    rotateObject(object, onComplete) {
        if (!object) return;

        const targetRotation = Math.round(object.rotation.y / (Math.PI / 2)) * (Math.PI / 2) + Math.PI / 2;
        gsap.to(object.rotation, {
            y: targetRotation,
            duration: 0.3,
            onUpdate: () => {},
            onComplete: () => {
                // После завершения анимации меняем местами x и z в bSize
                if (object.userData && object.userData.bSize instanceof THREE.Vector3) {
                    const temp = object.userData.bSize.x;
                    object.userData.bSize.x = object.userData.bSize.z;
                    object.userData.bSize.z = temp;
                }
                this._cutColumnSizeByBuildingSize(object);
                servObj.guideLines.recalculateLinesPosition(object, servObj.guideLines.findNeighbours(object));
                this.checkCollision();
                if (typeof onComplete === "function") onComplete();
            },
        });
    }
    changeSizeColumn(column, width, depth) {
        if (!column) return;
        column.scale.x = width;
        column.scale.z = depth;
        this._cutColumnSizeByBuildingSize(column);
    }
    deleteColumn(column) {
        if (!column) return;
        const findedColumn = this._columns.find((_) => _ == column);
        if (!findedColumn) return;

        this._columns = this._columns.filter((column) => column.uuid !== findedColumn.uuid);
        this._all = this._all.filter((column) => column.uuid !== findedColumn.uuid);
        findedColumn.removeFromParent();
        findedColumn.position.x = 100000;
    }
    deleteObject(object) {
        if (!object) return;
        // Ищем объект в this._objects по id, а не по ссылке
        const findedObject = this._objects.find((_) => _.id === object.id);
        if (!findedObject) {
            console.warn("Object not found in _objects");
            return;
        }

        const index = this._objects.findIndex((_) => _.id === findedObject.id);
        const index1 = this._all.findIndex((_) => _.id === findedObject.id);

        if (index !== -1 && index1 !== -1) {
            this._objects.splice(index, 1);
            this._all.splice(index1, 1);
            findedObject.removeFromParent();
        } else {
            console.warn("Object index not found in one of the arrays");
        }
    }

    // resetColumnsPosition(width, depth, height){
    //     if(this._width == width && this._depth == depth && this._height == height) return
    //     this.columns.map(_ => _.position.set(0,0,0))
    // }

    get maxX() {
        return this.floor.position.x + this._width / 2000 - 0.0008; // - this._wallDepth / 2
    }
    get minX() {
        return this.floor.position.x - this._width / 2000 + 0.0008; // + this._wallDepth / 2
    }
    get minZ() {
        return this.floor.position.z - this._depth / 2000 + 0.0008; // + this._wallDepth / 2
    }
    get maxZ() {
        return this.floor.position.z + this._depth / 2000 - 0.0008; // - this._wallDepth / 2
    }

    getColumnWorldSize(column) {
        if (!column) return new THREE.Vector3();
        const findedColumn = this._columns.find((_) => _ == column);
        if (!findedColumn) return new THREE.Vector3();

        return new THREE.Vector3(findedColumn.scale.x / 1000, findedColumn.scale.y / 1000, findedColumn.scale.z / 1000);
    }
    // getBoundaryGeometry(obj) {
    //     const box = new THREE.Box3().makeEmpty();
    //     box.expandByObject(obj);
    //     const size = box.getSize(new THREE.Vector3());
    //     return size;
    // }
    getBoundaryGeometry(obj) {
        const elements = [];
        obj.traverse((child) => {
            if (child === obj) return;
            if (child.userData?.position === "top") return;
            if (child.userData?.position === "bottom" || (child.name && child.name.includes("bazovii__osnovanie"))) {
                elements.push(child);
            }
        });
        elements.push(obj);
        const box = new THREE.Box3().makeEmpty();
        elements.forEach((el) => {
            el.traverse((child) => {
                if (!child.isMesh) return;
                expandByMorphMesh(box, child);
            });
        });
        if (box.isEmpty()) box.setFromObject(obj, true);
        const size = box.getSize(new THREE.Vector3());
        return size;
    }
    getColumnSize(column) {
        if (!column) return new THREE.Vector3();
        const findedColumn = this._columns.find((_) => _ == column);
        if (!findedColumn) return new THREE.Vector3();
        findedColumn.userData.bSize = this.getBoundaryGeometry(findedColumn);

        return new THREE.Vector3(findedColumn.scale.x, findedColumn.scale.y, findedColumn.scale.z);
    }
    hideWall(wallName) {
        if (!wallName) return;

        const wall = this._gltfModel.getObjectByName(wallName);
        if (!wall) return;
        wall.layers.set(1);
        wall.layers.enable(1);
        wall.children.map((_) => _.layers.set(1));
    }
    showAllWalls() {
        this.walls.map((wall) => {
            wall.layers.set(0);
            wall.layers.enable(0);
            wall.children.map((_) => _.layers.set(0));
        });
    }
    setWallsHiderMode(mode = "2D") {
        if (mode == "2D") this._is2dMode = true;
        if (mode == "3D") this._is2dMode = false;
    }
    _wallsHider(camera, delta = 0.2) {
        const distanse = {};

        this.walls.forEach((el) => {
            distanse[this._getDistanceBetweenObjects(camera, el)] = el.name;
        });

        let [min1, min2, min3] = Object.keys(distanse)
            .sort((a, b) => a - b)
            .slice(0, 3);

        let hide1 = distanse[min1];
        let hide2 = min3 - delta > min2 ? distanse[min2] : null;

        this.showAllWalls();
        this.hideWall(hide1);
        this.hideWall(hide2);

        if (this._is2dMode) this.showAllWalls();
    }
    wallsHider(camera) {
        // Кэшируем предыдущее состояние
        if (!this._lastWallsHider) {
            this._lastWallsHider = { angle: null, hidden: [], is2d: null };
        }

        const center = this.floor.position.clone();
        const camPos = camera.position.clone();
        const dir = new THREE.Vector2(camPos.x - center.x, camPos.z - center.z);
        let angle = Math.atan2(dir.y, dir.x);
        if (angle < 0) angle += Math.PI * 2;

        // Если угол изменился незначительно и режим не поменялся — ничего не делаем
        if (
            this._lastWallsHider.angle !== null &&
            Math.abs(this._lastWallsHider.angle - angle) < 0.01 &&
            this._lastWallsHider.is2d === this._is2dMode
        ) {
            return;
        }

        const sectorHalfWidth = (Math.PI * 3) / 8;
        const sectors = [
            { name: "wall_right", center: 0 },
            { name: "wall_bot", center: Math.PI / 2 },
            { name: "wall_left", center: Math.PI },
            { name: "wall_top", center: (3 * Math.PI) / 2 },
        ];

        function inSector(a, center, halfWidth) {
            let from = center - halfWidth;
            let to = center + halfWidth;
            if (from < 0) from += Math.PI * 2;
            if (to < 0) to += Math.PI * 2;
            if (from < to) return a >= from && a < to;
            return a >= from || a < to;
        }

        let visibleWalls = [];
        for (let i = 0; i < sectors.length; i++) {
            if (inSector(angle, sectors[i].center, sectorHalfWidth)) {
                visibleWalls.push(sectors[i].name);
            }
        }

        // Если скрытые стены не изменились и режим не изменился — ничего не делаем
        if (
            JSON.stringify(this._lastWallsHider.hidden) === JSON.stringify(visibleWalls) &&
            this._lastWallsHider.is2d === this._is2dMode
        ) {
            return;
        }
        this.showAllWalls();

        if (visibleWalls.length === 1) {
            this.hideWall(visibleWalls[0]);
        } else if (visibleWalls.length === 2) {
            this.hideWall(visibleWalls[0]);
            this.hideWall(visibleWalls[1]);
        }

        if (this._is2dMode) this.showAllWalls();

        // Обновляем кэш
        this._lastWallsHider.angle = angle;
        this._lastWallsHider.hidden = visibleWalls;
        this._lastWallsHider.is2d = this._is2dMode;
    }
    _resetAllObjectAppearances() {
        const activeValues = getActiveValues(servObj);

        const objectsToReset = [
            ...new Set([
                ...this._all,
                ...this._vertical_only,
                ...this._horizontal_only,
                ...this._objects,
                ...this.columns,
                ...activeValues,
            ]),
        ];

        objectsToReset.forEach((obj) => {
            obj.userData.isIntersect = false;
            obj.traverse((child) => {
                if (child.material) {
                    const materials = Array.isArray(child.material) ? child.material : [child.material];
                    materials.forEach((mat) => this._restoreMaterialToBase(mat));
                }
            });
        });
    }
    _restoreMaterialToBase(material) {
        if (material instanceof THREE.MeshStandardMaterial) {
            if (material.userData.originalColor) {
                // Ensure originalColor is a THREE.Color, clone if necessary before assignment
                if (material.userData.originalColor instanceof THREE.Color) {
                    material.color.copy(material.userData.originalColor);
                } else if (typeof material.userData.originalColor === "number") {
                    material.color.setHex(material.userData.originalColor);
                } else if (
                    typeof material.userData.originalColor === "object" &&
                    "r" in material.userData.originalColor
                ) {
                    // Handle cases where it might have been serialized
                    material.color.setRGB(
                        material.userData.originalColor.r,
                        material.userData.originalColor.g,
                        material.userData.originalColor.b,
                    );
                }
            }
            material.userData.isDarkened = false;
            material.emissive = new THREE.Color(0x000000); // Default no emissive
            material.emissiveIntensity = 1.0; // Default emissive intensity
        }
    }

    checkCollision() {
        // Сброс внешнего вида и флагов пересечения
        this._resetAllObjectAppearances();

        // Массив "запрещённых" столов, с которыми тумбы и стулья НЕ должны пересекаться (НЕ должны краснеть)
        const forbiddenTables = [
            "bazovii",
            "komfort",
            "spr",
            "spb",
            "ostrov",
            "verstak",
            "upakovochnii",
            "ksm",
            "raskroinii",
            "smk",
            "ssu",
        ];

        const allObjects = [
            ...this._all,
            ...this._vertical_only,
            ...this._horizontal_only,
            ...this._objects,
            ...this.columns,
        ];
        const uniqueObjects = Array.from(new Set(allObjects));
        uniqueObjects.forEach((obj) => {
            if (obj && obj.userData) obj.userData.isIntersect = false;
        });

        if (!servObj.isEditMode && !servObj.isRedactorMode) {
            // Сброс флагов пересечений
            for (const obj of this._all) {
                if (obj?.userData) obj.userData.isIntersect = false;
            }

            const objects = this._all;
            const count = objects.length;

            for (let i = 0; i < count - 1; i++) {
                const a = objects[i];
                if (!a?.userData?.bSize) continue;

                const aPos = a.position;
                const aSize = a.userData.bSize;
                const aHalfX = aSize.x / 2;
                const aHalfZ = aSize.z / 2;
                const aLeft = aPos.x - aHalfX;
                const aRight = aPos.x + aHalfX;
                const aTop = aPos.z + aHalfZ;
                const aBottom = aPos.z - aHalfZ;

                for (let j = i + 1; j < count; j++) {
                    const b = objects[j];
                    if (!b?.userData?.bSize) continue;

                    const bPos = b.position;
                    const bSize = b.userData.bSize;
                    const bHalfX = bSize.x / 2;
                    const bHalfZ = bSize.z / 2;
                    const bLeft = bPos.x - bHalfX;
                    const bRight = bPos.x + bHalfX;
                    const bTop = bPos.z + bHalfZ;
                    const bBottom = bPos.z - bHalfZ;

                    const collideX = aLeft < bRight && aRight > bLeft;
                    const collideZ = aBottom < bTop && aTop > bBottom;

                    if (collideX && collideZ) {
                        // --- Специальное правило для объектов, которые не должны пересекаться со столами ---
                        // Проверяем, является ли объект "a" или "b" тумбой или стулом
                        const aIsRestricted = ["tumbi", "stulya"].includes(a.userData.loadData?.folderName);
                        const bIsRestricted = ["tumbi", "stulya"].includes(b.userData.loadData?.folderName);

                        // Проверяем, является ли объект "a" или "b" столом из массива запрещённых
                        const aIsForbiddenTable =
                            a.userData.loadData?.folderName === "tables" &&
                            forbiddenTables.includes(a.name.split("__")[0]);
                        const bIsForbiddenTable =
                            b.userData.loadData?.folderName === "tables" &&
                            forbiddenTables.includes(b.name.split("__")[0]);

                        // Если пересечение между запрещённым объектом (тумба/стул) и запрещённым столом
                        if ((aIsRestricted && bIsForbiddenTable) || (bIsRestricted && aIsForbiddenTable)) {
                            // Ничего не делаем: такие пересечения игнорируются
                        } else {
                            // Для всех остальных объектов пересечение обрабатывается стандартно
                            a.userData.isIntersect = true;
                            b.userData.isIntersect = true;
                        }
                    }
                }
            }
        } else {
            const hitboxes = servObj.activeHitbox || [];
            const tempMatrix = new THREE.Matrix4();

            // Сброс флагов пересечения на родителях hitbox
            hitboxes.forEach((hitbox) => {
                if (hitbox.parent && hitbox.parent.userData) {
                    hitbox.parent.userData.isIntersect = false;
                }
            });

            // 1. Подготовка геометрии и мировых боксов
            this._scene.updateMatrixWorld(true);

            const worldBoxes = [];

            for (const obj of hitboxes) {
                if (!obj.geometry || !obj.parent) {
                    worldBoxes.push(null);
                    continue;
                }

                if (!obj.geometry.boundingBox) obj.geometry.computeBoundingBox();
                if (!obj.geometry.boundsTree) {
                    obj.geometry.computeBoundsTree = computeBoundsTree;
                    obj.geometry.computeBoundsTree();
                }

                // Создаём мировую AABB
                const worldBox = new THREE.Box3().copy(obj.geometry.boundingBox).applyMatrix4(obj.matrixWorld);
                worldBoxes.push(worldBox);
            }

            // 2. Сравнение пар
            for (let i = 0; i < hitboxes.length; i++) {
                const a = hitboxes[i];
                if (!a || !a.geometry || !a.parent) continue;

                for (let j = i + 1; j < hitboxes.length; j++) {
                    const b = hitboxes[j];
                    if (!b || !b.geometry || !b.parent) continue;
                    if (b.parent.name === a.parent.name && b.parent.id === a.parent.id) continue;

                    // Быстрая проверка по мировым AABB
                    if (!worldBoxes[i].intersectsBox(worldBoxes[j])) continue;

                    tempMatrix.copy(a.matrixWorld).invert().multiply(b.matrixWorld);

                    try {
                        if (a.geometry.boundsTree.intersectsGeometry(b.geometry, tempMatrix)) {
                            if (a.parent.userData) a.parent.userData.isIntersect = true;
                            if (b.parent.userData) b.parent.userData.isIntersect = true;
                        }
                    } catch (e) {
                        console.error("BVH intersectsGeometry error:", e, { a, b });
                    }
                }
            }
        }

        // Окраска объектов по результату пересечения
        uniqueObjects.forEach((obj) => {
            if (!obj || !obj.userData || obj.visible === false) return;
            if (obj.userData.isIntersect) {
                this.coloringObject(obj, this.redColor);
            }
        });

        servObj.activeHitbox.forEach((hitbox) => {
            // Находим самого верхнего родителя (root), у которого нет isChildren или нет родителя
            let root = hitbox.parent;
            while (root && root.userData && root.userData.isChildren && root.parent) {
                root = root.parent;
            }
            if (!root || !root.userData) return;

            // Проверяем, есть ли пересечение у кого-то из родителей в цепочке
            let hasIntersect = false;
            let obj = hitbox.parent;
            while (obj && obj !== root.parent) {
                if (obj.userData && obj.userData.isIntersect) {
                    hasIntersect = true;
                    break;
                }
                obj = obj.parent;
            }

            if (hasIntersect) {
                root.userData.isIntersect = true; // Устанавливаем флаг на корневом объекте
                this.coloringObject(root, this.redColor);
            } else {
                this.restoreSingleObjectColor(root);
            }
        });

        // Окраска активного объекта
        if (servObj.active && servObj.active.userData) {
            if (servObj.active.userData.isIntersect) {
                this.coloringObject(servObj.active, this.redColor);
            } else {
                this.coloringObject(servObj.active, this.blueColor);
            }
        }
    }

    changeWallsColor(color) {
        if (!(color instanceof THREE.Color)) {
            color = new THREE.Color(color);
        }
        this._wallColor = color;
        this.walls.map((wall) => (wall.children[0].material.color = color));
        this.columns.map((column) => {
            column.children[0].material.userData.originalColor?.copy(color);
            column.children[0].material.color.copy(color);

            column.children[0].material.userData.originalColor?.multiplyScalar(0.9);
            column.children[0].material.color.multiplyScalar(0.9);
        });
    }
    changeFloorColor(color) {
        if (!(color instanceof THREE.Color)) {
            color = new THREE.Color(color);
        }
        this._floorColor = color;
        this.floor.children[0].material.color = color;
    }
    initBuildingSize() {
        const lineSizesMaterial = new THREE.LineBasicMaterial({ color: this._sizeColor, linewidth: 10 });

        const _lineLimiter = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(this._lineLimiterLength, 0, 0),
            ]),
            lineSizesMaterial,
        );

        const lineLimiter = new Line2(
            new LineGeometry().fromLine(_lineLimiter),
            new LineMaterial({ color: this._sizeColor, linewidth: this._lineWidth }),
        );

        const _lineDepth = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([new THREE.Vector3()]),
            lineSizesMaterial,
        );
        const lineDepth = new Line2(
            new LineGeometry().fromLine(_lineDepth),
            new LineMaterial({ color: this._sizeColor, linewidth: this._lineWidth }),
        );

        lineDepth.material.worldUnits = false;
        lineDepth.material.needsUpdate = true;

        lineDepth.name = "Depth";
        this._scene.add(lineDepth);
        this._sizeLines["Depth"] = lineDepth;
        this._sizeLines["Depth"].add(lineLimiter.clone());
        this._sizeLines["Depth"].add(lineLimiter.clone());

        const _lineWidth = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([new THREE.Vector3()]),
            lineSizesMaterial,
        );
        const lineWidth = new Line2(
            new LineGeometry().fromLine(_lineWidth),
            new LineMaterial({ color: this._sizeColor, linewidth: this._lineWidth }),
        );

        lineWidth.material.worldUnits = false;
        lineWidth.material.needsUpdate = true;

        lineWidth.name = "Width";
        this._scene.add(lineWidth);
        this._sizeLines["Width"] = lineWidth;
        this._sizeLines["Width"].add(lineLimiter.clone());
        this._sizeLines["Width"].add(lineLimiter.clone());

        const _lineHeight = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([new THREE.Vector3()]),
            lineSizesMaterial,
        );
        const lineHeight = new Line2(
            new LineGeometry().fromLine(_lineHeight),
            new LineMaterial({ color: this._sizeColor, linewidth: this._lineWidth }),
        );

        lineHeight.material.worldUnits = false;
        lineHeight.material.needsUpdate = true;

        lineHeight.name = "Height";
        this._scene.add(lineHeight);
        this._sizeLines["Height"] = lineHeight;
        this._sizeLines["Height"].add(lineLimiter.clone());
        this._sizeLines["Height"].add(lineLimiter.clone());

        this.hideHeightSize();

        const containerSettings = {
            backgroundColor: new THREE.Color(1, 1, 1),
            width: 10.000001,
            height: 0.000001,
            justifyContent: "center",
            textAlign: "center",
            fontFamily: "./fonts/Roboto-msdf.json",
            fontTexture: "./fonts/Roboto-msdf.png",
            borderRadius: 0, // Убираем радиус границ
        };
        const textSettings = {
            content: "10",
            fontSize: 0.2,
            fontColor: new THREE.Color(this._sizeColor),
        };

        const containerDepth = new ThreeMeshUI.Block(containerSettings);
        const containerWidth = new ThreeMeshUI.Block(containerSettings);
        const containerHeight = new ThreeMeshUI.Block(containerSettings);
        const textDepth = new ThreeMeshUI.Text(textSettings);
        const textWidth = new ThreeMeshUI.Text(textSettings);
        const textHeight = new ThreeMeshUI.Text(textSettings);

        containerDepth.add(textDepth);
        containerWidth.add(textWidth);
        containerHeight.add(textHeight);

        this._sizeLines["Depth"].add(containerDepth);
        this._sizeLines["Width"].add(containerWidth);
        this._sizeLines["Height"].add(containerHeight);
    }
    hideBuildingSize() {
        for (let line in this._sizeLines) this._sizeLines[line].visible = false;
    }
    showBuildingSize() {
        for (let line in this._sizeLines) this._sizeLines[line].visible = true;
    }
    hideHeightSize() {
        this._sizeLines["Height"].visible = false;
    }
    showHeightSize() {
        this._sizeLines["Height"].visible = true;
    }
    updateBuildingSize() {
        const lineSizesMaterial = new THREE.LineBasicMaterial({ color: this._sizeColor, linewidth: 10 });

        const LineGeometryDepth = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(-this._width / 2000 - 1, 0, -this._depth / 2000),
                new THREE.Vector3(-this._width / 2000 - 1, 0, this._depth / 2000),
            ]),
            lineSizesMaterial,
        );

        this._sizeLines["Depth"].geometry = new LineGeometry().fromLine(LineGeometryDepth);

        // update limiter
        this._sizeLines["Depth"].children[0].position.x = -this._width / 2000 - 1 - this._lineLimiterLength / 2;
        this._sizeLines["Depth"].children[1].position.x = -this._width / 2000 - 1 - this._lineLimiterLength / 2;
        this._sizeLines["Depth"].children[0].position.z = -this._depth / 2000;
        this._sizeLines["Depth"].children[1].position.z = this._depth / 2000;

        const LineGeometryWidth = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(-this._width / 2000, 0, this._depth / 2000 + 1),
                new THREE.Vector3(this._width / 2000, 0, this._depth / 2000 + 1),
            ]),
            lineSizesMaterial,
        );
        this._sizeLines["Width"].geometry = new LineGeometry().fromLine(LineGeometryWidth);

        // update limiter
        this._sizeLines["Width"].children[0].rotation.y = Math.PI / 2;
        this._sizeLines["Width"].children[1].rotation.y = Math.PI / 2;
        this._sizeLines["Width"].children[0].position.x = -this._width / 2000;
        this._sizeLines["Width"].children[1].position.x = this._width / 2000;
        this._sizeLines["Width"].children[0].position.z = this._depth / 2000 + 1 + this._lineLimiterLength / 2;
        this._sizeLines["Width"].children[1].position.z = this._depth / 2000 + 1 + this._lineLimiterLength / 2;

        const LineGeometryHeight = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(-this._width / 2000 - 1, 0, -this._depth / 2000 - 1),
                new THREE.Vector3(-this._width / 2000 - 1, this._height / 1000, -this.depth / 2000 - 1),
            ]),
            lineSizesMaterial,
        );

        this._sizeLines["Height"].geometry = new LineGeometry().fromLine(LineGeometryHeight);
        // update limiter

        this._sizeLines["Height"].children[0].position.x = -this._width / 2000 - 1 - this._lineLimiterLength / 2;
        this._sizeLines["Height"].children[1].position.x = -this._width / 2000 - 1 - this._lineLimiterLength / 2;
        this._sizeLines["Height"].children[0].position.z = -this._depth / 2000 - 1;
        this._sizeLines["Height"].children[1].position.z = -this._depth / 2000 - 1;
        this._sizeLines["Height"].children[1].position.y = this._height / 1000;

        // this._sizeLines["Depth"].computeLineDistances()
        // this._sizeLines["Width"].computeLineDistances()
        // this._sizeLines["Height"].computeLineDistances()
    }
    updateBuildingSizeRotation() {
        // no lookAt !
        for (let line in this._sizeLines)
            this._getSizeComponents(this._sizeLines[line]).component.rotation.copy(this._scene.activeCamera.rotation);
    }
    _applySizesToBuilding(isHeightChanged = false) {
        this.floor.scale.x = this._width + this._wallDepth * 1000 * 2;
        this.floor.scale.z = this._depth + this._wallDepth * 1000 * 2;

        this.wallTop.scale.z = this._width + this._wallDepth * 1000 * 2;
        this.wallRight.scale.z = this._depth + this._wallDepth * 1000 * 2;
        this.wallBot.scale.z = this._width + this._wallDepth * 1000 * 2;
        this.wallLeft.scale.z = this._depth + this._wallDepth * 1000 * 2;

        this.wallTop.position.z = -this._depth / 2000 - this._wallDepth / 2;
        this.wallRight.position.x = this._width / 2000 + this._wallDepth / 2;
        this.wallBot.position.z = this._depth / 2000 + this._wallDepth / 2;
        this.wallLeft.position.x = -this._width / 2000 - this._wallDepth / 2;

        this.wallTop.scale.y = this._height;
        this.wallRight.scale.y = this._height;
        this.wallBot.scale.y = this._height;
        this.wallLeft.scale.y = this._height;

        this.wallTop.scale.x = this._wallDepth * 1000;
        this.wallBot.scale.x = this._wallDepth * 1000;
        this.wallRight.scale.x = this._wallDepth * 1000;
        this.wallLeft.scale.x = this._wallDepth * 1000;
        this.floor.scale.y = this._floorDepth;

        this.columns.map((column) => (column.scale.y = this._height));

        if (!isHeightChanged)
            this.columns.map((column) => {
                column.position.set(0, 0, 0);
                this._cutColumnSizeByBuildingSize(column);
            });

        if (Object.keys(this._sizeLines).length > 0) {
            this.updateBuildingSize();

            this._getSizeComponents(this._sizeLines["Depth"]).text.set({
                content: String(this._depth),
            });
            this._getSizeComponents(this._sizeLines["Width"]).text.set({
                content: String(this._width),
            });
            this._getSizeComponents(this._sizeLines["Height"]).text.set({
                content: String(this._height),
            });

            this._getSizeComponents(this._sizeLines["Depth"]).component.position.x =
                this._sizeLines["Depth"].geometry.attributes.instanceEnd.data.array[0] -
                0.1 * this._getSizeComponents(this._sizeLines["Depth"]).text.content.length;

            this._getSizeComponents(this._sizeLines["Height"]).component.position.x =
                this._sizeLines["Height"].geometry.attributes.instanceEnd.data.array[0] -
                0.1 * this._getSizeComponents(this._sizeLines["Height"]).text.content.length;

            this._getSizeComponents(this._sizeLines["Height"]).component.position.y = this._height / 2000;
            this._getSizeComponents(this._sizeLines["Height"]).component.position.z =
                this._sizeLines["Height"].geometry.attributes.instanceEnd.data.array[2];

            this._getSizeComponents(this._sizeLines["Width"]).component.position.z =
                this._sizeLines["Width"].geometry.attributes.instanceEnd.data.array[2] + 0.4;
        }
    }
    changeSizesColor(color) {
        this._sizeColor = color; // useless

        for (let line in this._sizeLines) {
            this._sizeLines[line].traverse((_) => {
                if (_.material && _.material.type == "LineMaterial") {
                    _.material.uniforms.diffuse.value = new THREE.Color(color);
                }
                if (_.fontMaterial) _.fontMaterial.uniforms.u_color.value = new THREE.Color(color);
            });
        }
    }
    _getSizeComponents(line) {
        return { component: line.children[2], text: line.children[2].children[1] };
    }
    _getDistanceBetweenObjects(obj1, obj2) {
        if (!obj1 || !obj2) return;
        return obj1.getWorldPosition(new THREE.Vector3()).distanceTo(obj2.getWorldPosition(new THREE.Vector3()));
    }
    _cutColumnSizeByBuildingSize(column) {
        if (!column) return;
        // const bSize = this.getBoundaryGeometry(column);
        const bSize = column.userData.bSize;
        // scale
        if (bSize.x * 1000 > this._width) column.scale.x = this._width;
        if (bSize.z * 1000 > this._depth) column.scale.z = this._depth;

        // position
        if (column.position.x + bSize.x / 2 > this.maxX) column.position.x = this.maxX - bSize.x / 2;
        if (column.position.x - bSize.x / 2 < this.minX) column.position.x = this.minX + bSize.x / 2;
        if (column.position.z + bSize.z / 2 > this.maxZ) column.position.z = this.maxZ - bSize.z / 2;
        if (column.position.z - bSize.z / 2 < this.minZ) column.position.z = this.minZ + bSize.z / 2;
    }
    coloringObject(obj, color) {
        // this.restoreObjectColor();
        // this.restoreColumnColor();

        obj.traverse((child) => {
            if (!child.material) return;
            this.changeMaterialEmissive(child, color);
        });
    }
    restoreColumnColor() {
        this.columns.forEach((ch) => {
            if (!ch.userData.isIntersect)
                ch.traverse((item) => {
                    if (!item.material) return;
                    const materials = Array.isArray(item.material) ? item.material : [item.material];

                    materials.forEach((material) => {
                        if (material?.emissiveMap) return;
                        if (material instanceof THREE.MeshStandardMaterial) {
                            if (material.userData.originalColor && material.userData.isDarkened) {
                                const originalColorValue = material.userData.originalColor;

                                if (originalColorValue instanceof THREE.Color) {
                                    material.color.copy(originalColorValue);
                                } else if (typeof originalColorValue === "number") {
                                    originalColorValue.toString(16);
                                    material.color.setHex(originalColorValue);
                                } else {
                                    console.error(
                                        "originalColor is neither Color nor number",
                                        material.name,
                                        originalColorValue,
                                    );
                                }
                                material.userData.isDarkened = false;
                            }
                            material.emissive = new THREE.Color(0x000000);
                            material.emissiveIntensity = 1;
                        }
                    });
                });
        });
    }
    restoreObjectColor() {
        [...this._objects, ...this.columns, ...this._vertical_only, ...this._horizontal_only].forEach((ch) => {
            if (!ch.userData.isIntersect)
                ch.traverse((item) => {
                    if (!item.material) return;
                    const materials = Array.isArray(item.material) ? item.material : [item.material];

                    materials.forEach((material) => {
                        if (material?.emissiveMap) return;
                        if (material instanceof THREE.MeshStandardMaterial) {
                            if (material.userData.originalColor && material.userData.isDarkened) {
                                const originalColorValue = material.userData.originalColor;
                                if (
                                    !(material.color instanceof THREE.Color) &&
                                    material.color &&
                                    typeof material.color === "object"
                                ) {
                                    // Восстановить THREE.Color из объекта
                                    material.color = new THREE.Color(
                                        material.color.r,
                                        material.color.g,
                                        material.color.b,
                                    );
                                }
                                if (originalColorValue instanceof THREE.Color) {
                                    material.color.copy(originalColorValue);
                                } else if (typeof originalColorValue === "number") {
                                    originalColorValue.toString(16);
                                    material.color.setHex(originalColorValue);
                                } else {
                                    console.error(
                                        "originalColor is neither Color nor number",
                                        material.name,
                                        originalColorValue,
                                    );
                                }
                                material.userData.isDarkened = false;
                            }
                            material.emissive = new THREE.Color(0x000000);
                            material.emissiveIntensity = 1;
                        }
                    });
                });
        });
    }
    restoreSingleObjectColor(obj) {
        if (!obj) return;
        if (!obj.userData.isIntersect) {
            obj.traverse((item) => {
                if (!item.material) return;
                const materials = Array.isArray(item.material) ? item.material : [item.material];

                materials.forEach((material) => {
                    if (material?.emissiveMap) return;
                    if (material instanceof THREE.MeshStandardMaterial) {
                        if (material.userData.originalColor && material.userData.isDarkened) {
                            const originalColorValue = material.userData.originalColor;
                            if (
                                !(material.color instanceof THREE.Color) &&
                                material.color &&
                                typeof material.color === "object"
                            ) {
                                // Восстановить THREE.Color из объекта
                                material.color = new THREE.Color(material.color.r, material.color.g, material.color.b);
                            }
                            if (originalColorValue instanceof THREE.Color) {
                                material.color.copy(originalColorValue);
                            } else if (typeof originalColorValue === "number") {
                                material.color.setHex(originalColorValue);
                            } else {
                                console.error(
                                    "originalColor is neither Color nor number",
                                    material.name,
                                    originalColorValue,
                                );
                            }
                            material.userData.isDarkened = false;
                        }
                        material.emissive = new THREE.Color(0x000000);
                        material.emissiveIntensity = 1;
                    }
                });
            });
        }
    }
    changeMaterialEmissive(object, color = this.blueColor) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => {
            if (material?.emissiveMap) return;
            if (material instanceof THREE.MeshStandardMaterial) {
                if (!material.userData.originalColor) {
                    material.userData.originalColor = material.color.clone();
                }
                if (!(material.color instanceof THREE.Color) && material.color && typeof material.color === "object") {
                    // Восстановить THREE.Color из объекта
                    material.color = new THREE.Color(material.color.r, material.color.g, material.color.b);
                }
                let newColor = material.color.clone();

                if (!material.userData.isDarkened) {
                    newColor.multiplyScalar(this.darkenMultiplier);
                    material.userData.isDarkened = true;
                    material.color = newColor;
                }
                material.emissive = new THREE.Color(color);
                material.emissiveIntensity = 0.5;
            }
        });
    }
    get buildingInfo() {
        return {
            width: this._width,
            depth: this._depth,
            height: this._height,
            wallColor:
                this._wallColor instanceof THREE.Color
                    ? `#${this._wallColor.getHexString()}`
                    : this._wallColor || this.DEFAULT_ROOM_COLORS.walls,
            floorColor:
                this._floorColor instanceof THREE.Color
                    ? `#${this._floorColor.getHexString()}`
                    : this._floorColor || this.DEFAULT_ROOM_COLORS.floor,
        };
    }
    get columnsData() {
        if (this._columns.length === 0) return [];
        return this._columns.map((column) => ({
            position: {
                x: column.position.x,
                y: column.position.y,
                z: column.position.z,
            },
            rotation: {
                x: column.rotation.x,
                y: column.rotation.y,
                z: column.rotation.z,
            },
            scale: {
                x: column.scale.x,
                y: column.scale.y,
                z: column.scale.z,
            },
        }));
    }
}

export default Building;
